import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { Form, Spinner } from 'react-bootstrap';
import { StepperButtons } from '../../brand/components/StepperButtons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AuthService } from '../../../service';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import { InternalRoutes } from '../../../../enum/InternalRoutes';
import { useSearchQuery } from '../../../../shared/hooks/useSearchQuery';
import { IAllStates } from '../../../../store/rootReducer';
import { setRegisterUser } from '../../../../store/register/influencer/influencerRegistrationSlice';
import { IRegistrationStepTwoRequest } from '../../brand/dto/IRegistrationStepTwoRequest';
import { IRegisterStepperProps } from '../../../../model/shared/IRegistration';
import { getSafeValue, isValidValue } from '../../../../shared/functions/Functions';

export function InfluencerStep2({ handleBackStep, stepIdNumber }: IRegisterStepperProps) {
    const [isLoading, setIsLoading] = useState(false);
    const utmCampaign = localStorage.getItem('utmCampaign') as string;
    const [confirmationToken, setConfirmationToken] = useState<IRegistrationStepTwoRequest>({
        confirmationToken: '',
        utmCampaign,
    });
    const influencerData = useSelector((state: IAllStates) => state.influencerRegistration);
    const token = getSafeValue(useSearchQuery('token'));
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!token) {
            navigate('/');
            return;
        }
        AuthService.getInfluencerData(token)
            .then(response => {
                dispatch(setRegisterUser(response));
                if (response?.isEnabled) {
                    navigate(`${InternalRoutes.INFLUENCER_REGISTRATION}/${stepIdNumber + 1}?token=${token}`);
                }
            })
            .catch(error => ErrorToast(error));
    }, []);

    const handleNextStep = () => {
        if (!token) {
            ErrorToast('Invalid or missing token. Please restart registration.');
            return;
        }

        AuthService.verifyInfluencer(token, confirmationToken)
            .then(response => {
                if (response) {
                    navigate(
                        `${InternalRoutes.INFLUENCER_REGISTRATION}/${stepIdNumber + 1}?token=${
                            response?.verificationUUID
                        }`
                    );
                    localStorage.removeItem('utmCampaign');
                    dispatch(setRegisterUser(response));
                    SuccessToast(t('auth.register.brand.step2.successCode'));
                }
            })
            .catch(error => ErrorToast(error));
    };
    const resendVerify = () => {
        setIsLoading(true);
        AuthService.resetVerifyCodeInfluencer(influencerData?.user?.email ?? '')
            .then(() => {
                SuccessToast(t('auth.register.brand.step1.pleaseCheckEmail'));
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsLoading(false));
    };

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setConfirmationToken(prev => ({ ...prev, [name]: value }));
    };
    return (
        <>
            <Form
                className="w-100"
                onSubmit={e => {
                    e.preventDefault();
                    handleNextStep();
                }}
            >
                <div className="row justify-content-center">
                    <div className="col-md-7">
                        <TextField
                            label="Code"
                            onChange={changeHandler}
                            name="confirmationToken"
                            variant="outlined"
                            size="small"
                            required
                            className="w-100 my-2"
                        />
                        <div className="d-flex align-items-center justify-content-between">
                            <span className="text-muted">{t('auth.register.brand.step2.checkSpamFolder')}</span>
                            <a
                                className="d-flex align-items-center justify-content-center cursor-pointer"
                                onClick={resendVerify}
                            >
                                {isLoading ? (
                                    <>
                                        Loading...{' '}
                                        <Spinner animation="border" size="sm" role="status" className="ml-1" />
                                    </>
                                ) : (
                                    t('auth.register.brand.step2.reSend')
                                )}
                            </a>
                        </div>
                    </div>
                    <div className="col-12 mt-4">
                        <StepperButtons
                            currentStep={stepIdNumber - 1}
                            handleNextStep={handleNextStep}
                            isBackHidden={true}
                            handleBackStep={handleBackStep}
                        />
                    </div>
                </div>
            </Form>
        </>
    );
}
